import { DecimalPipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { writeFile, utils as XLSXUtils } from 'xlsx';
import { DeviceStateService } from './device-state.service';
import DriversService from './drivers.service';
import { KeywordStateService } from './keyword-state.service';
import LandmarksCategoriesService from './landmarks-categories.service';
import LandmarksService from './landmarks.service';
import UnitsService from './units.service';

import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

import { TableColumn } from '../component/table/table-column';
import { DateHelper } from '../utils/DateHelper';
import AreaPipe from '../utils/pipes/area.pipe';
import DurationPipe from '../utils/pipes/duration.pipe';
import DynamicDatePipe from '../utils/pipes/dynamic-date.pipe';
import ImperialPipe from '../utils/pipes/imperial.pipe';
import ValueByKeyPipe from '../utils/pipes/value-by-key.pipe';
import { PreferenceService } from './preference.service';

export type ExportFormat = 'pdf' | 'csv' | 'print' | 'excel';

export interface TableExportConfig<D> {
  /** Data rows to export */
  data: D[];

  /** Visible columns (already filtered by `selected`) */
  columns: TableColumn<D>[];

  /** Current sort state (optional, for pre-sorting export data) */
  sortParameters?: Sort;

  /** i18n key used as document title prefix */
  printTitle?: string;

  /** Optional observed period for PDF header */
  observedPeriod?: { start: number; end: number };

  /** Limit export to selected rows only (pass `selection.selected`). If empty/null → export all. */
  selectedRows?: D[];
}

@Injectable()
export abstract class SaveListService {
  private _unitsService = inject(UnitsService);
  private _deviceStateService = inject(DeviceStateService);
  private _landmarksCategoriesService = inject(LandmarksCategoriesService);
  protected _landmarksService = inject(LandmarksService);
  private _keywordStateService = inject(KeywordStateService);
  protected _driversService = inject(DriversService);

  protected _duration = inject(DurationPipe);
  protected _valueByKeyPipe = inject(ValueByKeyPipe);
  protected _imperialPipe = inject(ImperialPipe);
  protected _translate = inject(TranslateService);
  protected _areaPipe = inject(AreaPipe);
  protected _dynamicDatePipe = inject(DynamicDatePipe);
  protected _decimalPipe = inject(DecimalPipe);
  protected _preferenceService = inject(PreferenceService);

  // ── Generic table export entry point ───────────────────────────────
  public exportTableFile<D>(format: ExportFormat, config: TableExportConfig<D>): void {
    const localeStr = this._preferenceService.$currentLang();
    const { rows, headers, docTitle } = this._prepareExportData(format, config, localeStr);

    switch (format) {
      case 'pdf':
        this._savePdf(headers, rows, docTitle, localeStr, config);
        break;
      case 'csv':
      case 'print':
        this._saveCsv([headers, ...rows], docTitle);
        break;
      case 'excel':
        this._saveExcel(headers, rows, docTitle);
        break;
    }
  }

  // ── CSV / Excel / file primitives ──────────────────────────────────
  public exportToCSV(x: unknown[][], title?: string) {
    const csv = this.convertToCSV(x);
    this._exportCSV(csv, title);
  }

  public convertToCSV(data: unknown[][]): string {
    const csv =
      '\uFEFF' +
      data
        .map(function (line) {
          line = line.map(lineString =>
            typeof lineString === 'string' ? lineString.replace(/[\n\r;]/g, '') : lineString
          );
          return line.join(';') + ';' + '\r\n';
        })
        .join('');

    return csv;
  }

  public exportExcelFile(data: unknown[], excelTitle: string) {
    const worksheet = XLSXUtils.json_to_sheet(data);
    const workbook = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(workbook, worksheet);
    writeFile(workbook, excelTitle);
  }

  // ── Private: CSV file download ─────────────────────────────────────
  private _exportCSV(csv: string, title?: string) {
    const csvTitle = title ?? 'COYOTE_FLEET_CONNECT.csv';
    const download = document.createElement('a');
    download.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv));
    download.setAttribute('download', csvTitle);
    document.body.appendChild(download);
    download.click();
    document.body.removeChild(download);
  }

  // ── Private: data preparation ──────────────────────────────────────
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private _prepareExportData<D>(
    format: ExportFormat,
    config: TableExportConfig<D>,
    localeStr: string
  ): { rows: unknown[][]; headers: string[]; docTitle: string; totals: Record<string, unknown> } {
    const columns = config.columns;
    const dataProvider = [...config.data];

    if (config.sortParameters?.active && config.sortParameters?.direction) {
      const keyName = config.sortParameters.active;
      dataProvider.sort((a, b) => {
        if (config.sortParameters.direction === 'asc') {
          if (!a[keyName]) return -1;
          return a[keyName] - b[keyName];
        }
        if (config.sortParameters.direction === 'desc') {
          return b[keyName] - a[keyName];
        }
        return 0;
      });
    }

    const totalColumns: string[] = columns.filter(col => col.sum).map(c => c.dataKey);
    const isSum = totalColumns.length > 0;
    const dataTotal: Record<string, unknown> = {};

    const rows: unknown[][] = [];
    const hasSelection = config.selectedRows && config.selectedRows.length > 0;
    const selectedUids = hasSelection
      ? new Set((config.selectedRows as { uid: number }[]).map(r => r.uid))
      : null;

    for (const data of dataProvider) {
      if (hasSelection && !selectedUids.has((data as unknown as { uid: number }).uid)) continue;

      const tempObj: unknown[] = [];
      for (const col of columns) {
        this._computeSum(isSum, totalColumns, dataTotal, dataProvider, col);
        const value = this._valueByKeyPipe.transform(data, col.dataKey);

        if (
          (value != 0 && value != undefined && value != null) ||
          col.config?.isMissionStatus ||
          col.config?.isBoolean
        ) {
          tempObj.push(this._formatValue(col, value, data, localeStr));
        } else {
          tempObj.push('');
        }
      }
      rows.push(tempObj);
    }

    const finalRows = isSum ? [...rows, Object.values(dataTotal)] : rows;
    const printTitle = config.printTitle;
    const docTitle = `${printTitle ? this._translate.instant(printTitle) : 'export'}-${new Date().toLocaleDateString(localeStr)}.${format === 'excel' ? 'xlsx' : format}`;
    const headers = columns.map(column => this._translate.instant(column.name));

    return { rows: finalRows, headers, docTitle, totals: dataTotal };
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity, @typescript-eslint/no-explicit-any
  private _formatValue<D>(col: TableColumn<D>, value: any, data: D, localeStr: string): unknown {
    if (!col.config) return value;

    if (col.config.isDate) return this._dynamicDatePipe.transform(value * 1000, col.date.format);
    if (col.config.isDistance) return this._imperialPipe.transform(value, 'distance');
    if (col.config.isSpeed) return this._imperialPipe.transform(value, 'speed', 0);
    if (col.config.isBoolean) return this._translate.instant(`COMMON.${value ? 'YES' : 'NO'}`);
    if (col.config.isDuration) return this._duration.transform(value * 1000);
    if (col.config.isTranslated)
      return this._translate.instant((data as Record<string, unknown>)[col.dataKey] as string);
    if (col.config.isMissionStatus)
      return this._getTextFromMissionStatus(data as unknown as Record<string, unknown>);
    if (col.config.isCss) return value.name[localeStr];
    if (col.config.isKeywords) return this._transformKeywords(value);
    if (col.config.isAlarmType) return this._translate.instant(value?.label);
    if (col.config.isDriver) return this._driversService.$valuesRecord()?.[value]?.label;
    if (col.config.isPoi || col.config.isZoi) return this._landmarksService.$landmarksById()?.[value]?.label;
    if (col.config.isService) return this._unitsService.$unitsById()?.[value]?.label;
    if (col.config.isDeviceIcon || col.config.isLandmarkIcon) return '';
    if (col.config.isIcon) return this._translate.instant(col.css.iconTrad[value]);
    if (col.config.isDevice) {
      return value?.length > 1
        ? value.map(d => this._deviceStateService.get(d)?.registration || d).join(' / ')
        : this._deviceStateService.get(value)?.registration;
    }
    if (col.config.isArea) return this._areaPipe.transform(value, 'kilometers', 3, false);
    if (col.config.isCategory) return this._landmarksCategoriesService.$categoriesById()?.[value]?.label;
    if (col.config.isLiter) {
      const floatNumber = this._decimalPipe.transform(value.label ?? 0, '1.0-0') ?? '0';
      return floatNumber + ' L';
    }

    return value;
  }

  private _computeSum<D>(
    isSum: boolean,
    totalColumns: string[],
    dataTotal: Record<string, unknown>,
    dataProvider: D[],
    col: TableColumn<D>
  ): void {
    if (!isSum) return;

    if (totalColumns.includes(col.dataKey)) {
      dataTotal[col.dataKey] = dataProvider
        .map(t => (t as Record<string, number>)[col.dataKey])
        .reduce((acc, v) => acc + v, 0);
      if (col.config?.isDuration) {
        dataTotal[col.dataKey] = this._duration.transform((dataTotal[col.dataKey] as number) * 1000);
      }
      if (col.config?.isDistance) {
        dataTotal[col.dataKey] = `${dataTotal[col.dataKey]}km`;
      }
    } else {
      dataTotal[col.dataKey] = '-';
    }
  }

  private _saveCsv(dataWithHeaders: unknown[][], docTitle: string): void {
    this.exportToCSV(dataWithHeaders, docTitle);
  }

  private _saveExcel(headers: string[], rows: unknown[][], docTitle: string): void {
    const data = rows.map(row =>
      headers.reduce(
        (acc, key, index) => {
          acc[key] = row[index];
          return acc;
        },
        {} as Record<string, unknown>
      )
    );
    this.exportExcelFile(data, docTitle);
  }

  private _savePdf<D>(
    headers: string[],
    rows: unknown[][],
    docTitle: string,
    localeStr: string,
    config: TableExportConfig<D>
  ): void {
    const img = new Image();
    img.src = 'assets/logo/cbs-row-white.png';
    const doc = new jsPDF('landscape', 'mm');
    const printTitle = config.printTitle;

    let pageTitle = `${printTitle ? this._translate.instant(printTitle) : 'export'} - ${this._translate.instant('TABLE.EXPORTTHE')} ${new Date().toLocaleDateString(localeStr)}`;

    if (config.observedPeriod?.start && config.observedPeriod?.end) {
      const start = this._dynamicDatePipe.transform(new DateHelper(config.observedPeriod.start), 'shortDate');
      const end = this._dynamicDatePipe.transform(new DateHelper(config.observedPeriod.end), 'shortDate');
      pageTitle += ` - ${this._translate.instant('REPORT.OBSERVATIONPERIOD')} : ${start}`;
      if (start !== end) {
        pageTitle += ` ${this._translate.instant('TABLE.TO')} ${end}`;
      }
    }

    const hasSum = (config.columns ?? []).some(c => c.sum);
    const totalPagesExp = '{total_pages_count_string}';

    autoTable(doc, {
      head: [headers],
      body: rows,
      didParseCell: hasSum
        ? function (data) {
            const bodyRows = data.table.body;
            if (data.row.index === bodyRows.length - 1) {
              if (data.row.raw[0] === '-') data.row.cells[0].text = ['Total'];
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fontSize = 12;
              data.cell.styles.fillColor = [200, 215, 235];
            }
          }
        : null,
      didDrawPage: function (data) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(12);
        img.onload = function () {
          doc.addImage(img, 'png', data.settings.margin.left, 15, 35, 8);
        };
        const pageTitleWidth = doc.getTextDimensions(pageTitle).w;
        doc.text(pageTitle, doc.internal.pageSize.width - (data.settings.margin.right + pageTitleWidth), 22);
        const pageCount = doc.getNumberOfPages();
        let str = 'Page ' + pageCount;

        if (typeof doc.putTotalPages === 'function') {
          str = str + '/' + totalPagesExp;
        }
        const pageCountWidth = doc.getTextDimensions(str).w / 3;
        doc.setFontSize(10);
        doc.text(
          str,
          doc.internal.pageSize.width - (data.settings.margin.right + pageCountWidth),
          doc.internal.pageSize.height - 10
        );
      },
      margin: { top: 30 },
    });

    if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages(totalPagesExp);
    }
    doc.save(docTitle);
  }

  private _transformKeywords(keywordsIdProvided: number[]): string[] {
    const keywordsMaintenance: string[] = [];
    const keywords = this._keywordStateService.$keywords();
    for (const keywordId of keywordsIdProvided) {
      keywordsMaintenance.push(keywords[keywordId].label);
    }
    return keywordsMaintenance;
  }

  private _getTextFromMissionStatus(dataState: Record<string, unknown>): string {
    if (dataState.status === 'B') return 'Brouillon';
    if (dataState.status === 'C') return 'Annulé';

    switch (dataState.last_status) {
      case 'E':
        return 'Envoyé';
      case 'R':
        return 'Reçu';
      case 'L':
        return 'Lu';
      case 'M':
        return 'Modifié';
      case 'A':
        return 'En cours';
      case 'T':
        return 'Terminé';
      case 'F':
        return 'Refusé';
      case 'C':
        return 'Annulé';
      case 'K':
        return 'Annulé';
      case 'S':
        return 'Note';
      case 'N':
        return 'Nouvelle mission';
      case 'Q':
        return 'Signature';
      case 'P':
        return 'Photo';
      case 'D':
        return 'Chgt destination';
      case 'C_PENDING':
        return 'Annulé (demande)';
      default:
        return null;
    }
  }
}
